import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import NetworkBackground from '../components/NetworkBackground';
import '../styles/Landing.css';

const PILLARS = [
  {
    title: 'Investigation Cases',
    desc: 'Structured workspaces for scam, fraud, phishing, impersonation, and threat-actor investigations — with timeline, notes, and audit trail.',
    tags: ['Case types', 'Timeline', 'Audit log'],
    accent: 'cyan',
  },
  {
    title: 'Controlled Personas',
    desc: 'Cover identities for authorized engagement, with Cloaked integration for shared inboxes and platform-specific credentials.',
    tags: ['Personas', 'Cloaked sync', 'Engagement'],
    accent: 'violet',
  },
  {
    title: 'Evidence & IOCs',
    desc: 'Quarantined artifact vault, indicator catalog, actor linkage, and in-house intelligence enrichment.',
    tags: ['Evidence vault', 'IOCs', 'Enrichment'],
    accent: 'emerald',
  },
  {
    title: 'Intelligence Reports',
    desc: 'Client-ready summaries, executive exports, and structured findings built from case evidence and analyst notes.',
    tags: ['Reports', 'Export', 'Briefings'],
    accent: 'amber',
  },
];

const STEPS = [
  {
    n: '1',
    title: 'Open a Case',
    desc: 'Define scope, case type, and stakeholders. Every action is logged from the first minute.',
  },
  {
    n: '2',
    title: 'Engage & Collect',
    desc: 'Deploy personas, capture interactions, ingest evidence, and enrich indicators through integrated tooling.',
  },
  {
    n: '3',
    title: 'Analyze & Report',
    desc: 'Build the timeline, link actors and IOCs, and produce intelligence outputs for decision-makers.',
  },
];

function Landing() {
  useEffect(() => {
    document.title = 'Hivemind Subterfuge Platform';
  }, []);

  return (
    <div className="landing-page">
      <div className="landing-bg" aria-hidden="true">
        <NetworkBackground density={0.55} />
      </div>

      <header className="landing-topbar">
        <div className="landing-topbar-inner">
          <Link to="/" className="landing-brand">
            <span className="landing-brand-mark">◈</span>
            HiveMind
          </Link>
          <nav className="landing-topnav">
            <a href="#capabilities">Capabilities</a>
            <a href="#workflow">Workflow</a>
            <Link to="/login" className="landing-btn landing-btn-primary landing-btn-sm">
              Operator Sign In
            </Link>
          </nav>
        </div>
      </header>

      <main>
        <section className="landing-hero">
          <div className="landing-hero-inner">
            <div className="landing-badge">
              <span className="landing-badge-dot" />
              Authorized investigative &amp; deception operations only
            </div>
            <h1 className="landing-title">Hivemind Subterfuge Platform</h1>
            <p className="landing-tagline">Unified investigation, enrichment, and controlled engagement for Cipher Cortex operations.</p>
            <p className="landing-lede">
              Hivemind Subterfuge Platform unifies <strong>cases</strong>, <strong>controlled personas</strong>,{' '}
              <strong>evidence</strong>, and <strong>intelligence reporting</strong> — plus operational modules
              for decoy assets, intake endpoints, and analyst workflows. Built for lawful, authorized
              fraud and threat-actor investigations.
            </p>
            <div className="landing-hero-actions">
              <Link to="/login" className="landing-btn landing-btn-primary">
                Operator Sign In
              </Link>
              <a
                href="https://ciphercortex.com/contact.html"
                className="landing-btn landing-btn-secondary"
                target="_blank"
                rel="noopener noreferrer"
              >
                Request an Investigation Briefing
              </a>
            </div>
          </div>
        </section>

        <section id="workflow" className="landing-section">
          <div className="landing-section-head">
            <h2>How It Works</h2>
            <p>From intake to intelligence deliverable in three phases</p>
          </div>
          <div className="landing-steps">
            {STEPS.map((step) => (
              <article key={step.n} className="landing-step">
                <div className="landing-step-num">{step.n}</div>
                <h3>{step.title}</h3>
                <p>{step.desc}</p>
              </article>
            ))}
          </div>
        </section>

        <section id="capabilities" className="landing-section landing-section-band">
          <div className="landing-section-head">
            <h2>Platform Capabilities</h2>
            <p>Investigation management with operational depth</p>
          </div>
          <div className="landing-grid landing-grid-2">
            {PILLARS.map((p) => (
              <article key={p.title} className={`landing-card landing-card-${p.accent}`}>
                <h3>{p.title}</h3>
                <p>{p.desc}</p>
                <div className="landing-tags">
                  {p.tags.map((t) => (
                    <span key={t}>{t}</span>
                  ))}
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="landing-section">
          <div className="landing-section-head">
            <h2>Operations Modules</h2>
            <p>Legacy HiveMind C2 continuity for controlled decoy operations</p>
          </div>
          <div className="landing-grid landing-grid-3">
            <article className="landing-mini-card">
              <h3>Decoy Assets &amp; Listeners</h3>
              <p>Manage agents, intake endpoints, tasks, and live operator console access.</p>
            </article>
            <article className="landing-mini-card">
              <h3>Intelligence Engine</h3>
              <p>50+ in-house enrichment tools — IPs, domains, emails, phones, wallets, panel fingerprints, and more.</p>
            </article>
            <article className="landing-mini-card">
              <h3>Audit &amp; Compliance</h3>
              <p>Role-based access, MFA, and full platform audit logging for every operator action.</p>
            </article>
          </div>
        </section>

        <section className="landing-stats">
          <div className="landing-stats-inner">
            <div>
              <p className="landing-stat-value">Cases</p>
              <p className="landing-stat-label">Investigation workspaces</p>
            </div>
            <div>
              <p className="landing-stat-value">Personas</p>
              <p className="landing-stat-label">Controlled identities</p>
            </div>
            <div>
              <p className="landing-stat-value">50+</p>
              <p className="landing-stat-label">Enrichment tools</p>
            </div>
            <div>
              <p className="landing-stat-value">Full</p>
              <p className="landing-stat-label">Audit trail</p>
            </div>
          </div>
        </section>

        <section className="landing-cta">
          <div className="landing-cta-inner">
            <h2>Authorized operators only</h2>
            <p>
              Hivemind Subterfuge Platform is for Cipher Cortex personnel and authorized partners conducting defensive
              investigations. Sign in with your operator credentials or request a briefing.
            </p>
            <div className="landing-hero-actions">
              <Link to="/login" className="landing-btn landing-btn-primary">
                Operator Sign In
              </Link>
              <a
                href="https://ciphercortex.com/contact.html"
                className="landing-btn landing-btn-secondary"
                target="_blank"
                rel="noopener noreferrer"
              >
                Contact Cipher Cortex
              </a>
            </div>
          </div>
        </section>
      </main>

      <footer className="landing-footer">
        <div className="landing-footer-inner">
          <div>
            <p className="landing-footer-brand">Hivemind Subterfuge Platform · Cipher Cortex LLC</p>
            <p className="landing-footer-muted">Investigation, enrichment &amp; controlled engagement</p>
          </div>
          <div className="landing-footer-links">
            <Link to="/login">Operator Sign In</Link>
            <a href="https://ciphercortex.com/" target="_blank" rel="noopener noreferrer">Cipher Cortex</a>
            <a href="https://interceptcell.com/" target="_blank" rel="noopener noreferrer">Intercept Cell</a>
          </div>
        </div>
        <p className="landing-legal">
          This system is for authorized operators only. Unauthorized access is prohibited and may be prosecuted.
        </p>
      </footer>
    </div>
  );
}

export default Landing;
